import BackButton from '@/components/back-button';
import { DataTable } from '@/components/data-table';
import ContentLayout from '@/components/layout/ContentLayout';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTE } from '@/routes/constants';
import { getAssignmentListByTeacher, getStudentAssignmentStats, getStudentPracticeStats } from '@/services/generatedAPIs';
import { AssignmentModel, AssignmentStats, StudentAssignmentStat } from '@/services/generatedAPIs.schemas';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { t } from 'i18next';
import { ArrowUpDown, Check, Clock3, X } from 'lucide-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { ClassTableData } from './HomeworkPage';

type TableData = {
  name: string;
  classID?: number;
  questionCount: number;
  correctCount: number;
}

const PracticeClassViewInner = ({ state }: { state: ClassTableData }) => {
  const { t, i18n } = useTranslation();
  const { getCurrentUser } = useAuth();
  let [tableData, setTableData] = React.useState<TableData[]>();
  let [columns, setColumns] = React.useState<ColumnDef<TableData>[]>([]);

  useEffect(() => {
    if (tableData && tableData.length > 0) {
      return;
    }

    getStudentPracticeStats({ classInfoId: state.id }).then((rsp) => {
      if (!tableData && rsp.code === 200 && rsp.data && rsp.data.studentAssignmentStatList.length > 0) {
        const studentAssignmentStatList: StudentAssignmentStat[] = [];
        for (const studentAssignmentStat of rsp.data.studentAssignmentStatList) {
          studentAssignmentStatList.push({
            student: studentAssignmentStat.student,
            question: studentAssignmentStat.question.filter(q => q.isCompleted)
          })
        }
        const hasClassID = studentAssignmentStatList.filter((stat) => stat.student.studentClassNumber).length > 0;
        columns = [
          {
            id: "select",
            header: ({ table }) => (
              //   <Checkbox
              //     checked={
              //       table.getIsAllPageRowsSelected() ||
              //       (table.getIsSomePageRowsSelected() && "indeterminate")
              //     }
              //     onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
              //     aria-label="Select all"
              //   />
              <></>
            ),
            cell: ({ row }) => (
              //   <Checkbox
              //     checked={row.getIsSelected()}
              //     onCheckedChange={(value) => row.toggleSelected(!!value)}
              //     aria-label="Select row"
              //   />
              <></>
            ),
            enableSorting: false,
            enableHiding: false,
          },
          {
            accessorKey: "name",
            header: ({ column }) => {
              return (
                <Button
                  variant="ghost"
                  onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                  {t("user.name")}
                  <ArrowUpDown />
                </Button>
              )
            },
            cell: ({ row }) => (
              <div>{row.original.name}</div>
            ),
          },
        ]

        if (hasClassID) {
          columns.push({
            accessorKey: "classID",
            header: ({ column }) => {
              return (
                <Button
                  variant="ghost"
                  onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                  {t("common.classID")}
                  <ArrowUpDown />
                </Button>
              )
            },
            cell: ({ row }) => (
              <div>{row.original.classID}</div>
            ),
          })
        }

        columns.push({
            accessorKey: "qcount",
            header: ({ column }) => {
              return (
                <Button
                  variant="ghost"
                  onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                  {t("teacher.qcount")}
                  <ArrowUpDown />
                </Button>
              )
            },
            cell: ({ row }) => (
              <div>{row.original.questionCount}</div>
            ),
      });

        columns.push({
            accessorKey: "correctness",
            header: ({ column }) => {
              return (
                <Button
                  variant="ghost"
                  onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                  {t("common.correctness")}
                  <ArrowUpDown />
                </Button>
              )
            },
            cell: ({ row }) => (
              <div>{(row.original.questionCount ? (row.original.correctCount * 100.0 / row.original.questionCount).toFixed(0) : 0) + '%'}</div>
            ),
      });
        setColumns(columns);

        tableData = [];
        for (let i = 0; i < studentAssignmentStatList.length; i++) {
          let data: TableData = { name: studentAssignmentStatList[i].student.name!, classID: studentAssignmentStatList[i].student.studentClassNumber, questionCount: studentAssignmentStatList[i].question.length, correctCount: studentAssignmentStatList[i].question.filter(q => q.isCorrectAnswer).length };
          tableData.push(data);
        }
        setTableData(tableData)
      }
    }
    ).catch((e) => {
      console.error("Error fetching assignment list", e);
    }
    );
  });

  return (
    <ContentLayout title={t("teacher.classview")}>
      <div className="mx-auto space-y-6">
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="mb-4">
            <BackButton />
          </div>
          <DataTable data={tableData ?? []} columns={columns} filterColumn='name' filterName={t('user.name')} />
        </div>
      </div>
    </ContentLayout>
  );
};

const PracticeClassView = () => {
  const state = useLocation().state as ClassTableData | null;
  const navigate = useNavigate();

  const isValidState = (s: any): s is ClassTableData => {
    return !!s && typeof s.id === 'number' && typeof s.name === 'string' && typeof s.studentCount === 'number';
  };

  React.useEffect(() => {
    if (!isValidState(state)) {
      navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
    }
  }, [state, navigate]);

  if (!isValidState(state)) {
    return <LoadingOverlay />;
  }

  return <PracticeClassViewInner state={state} />;
};

export default PracticeClassView;